/**
 * TranslationsPanel — Phase G2 suivi (2026-09-21).
 * Back-office FR. Un bouton clair « Générer les traductions » + tableau
 * simple des 5 statuts (EN/ES/IT/ZH-CN/JA) : AI_TRANSLATED, HUMAN_REVIEWED,
 * OUTDATED (dérivé du source_hash). Jamais de traduction à la frappe.
 *
 * Props :
 *   - resource: "articles" | "practical-sheets"
 *   - id: string
 *   - disabled?: boolean (contenu FR pas prêt, brouillon vide, etc.)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Loader2, Languages, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LANGS = ["en", "es", "it", "zh-cn", "ja"];

const STATUS_LABEL = {
  AI_TRANSLATED: { text: "Traduction IA", cls: "bg-blue-100 text-blue-800" },
  HUMAN_REVIEWED: { text: "Validée humain", cls: "bg-green-100 text-green-800" },
  OUTDATED: { text: "À mettre à jour", cls: "bg-amber-100 text-amber-900" },
  NONE: { text: "Absente", cls: "bg-gray-100 text-gray-600" },
};

export default function TranslationsPanel({ resource, id, disabled = false }) {
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [runningLang, setRunningLang] = useState(null);
  const [runningAll, setRunningAll] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/${resource}/${id}/translations`);
      setStatuses(data.translations || {});
    } catch (e) {
      toast.error("Impossible de lire les statuts de traduction");
    } finally {
      setLoading(false);
    }
  }, [resource, id]);

  useEffect(() => { load(); }, [load]);

  const translateOne = async (lang, force = false) => {
    setRunningLang(lang);
    try {
      const { data } = await axios.post(
        `${API}/admin/${resource}/${id}/translate`,
        { lang, force }
      );
      if (data.skipped) toast.info(`${lang.toUpperCase()} — protégée (validation humaine)`);
      else toast.success(`${lang.toUpperCase()} — ${data.status || "générée"}`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Traduction échouée";
      toast.error(typeof msg === "string" ? msg : "Traduction échouée");
    } finally {
      setRunningLang(null);
    }
  };

  const translateAll = async () => {
    if (disabled) {
      toast.error("Le contenu FR doit être prêt avant de générer les traductions");
      return;
    }
    setRunningAll(true);
    try {
      for (const lg of LANGS) {
        await translateOne(lg, false);
      }
    } finally {
      setRunningAll(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid="translations-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Languages className="h-4 w-4" />
          <div className="font-medium text-sm">Traductions (FR → EN/ES/IT/ZH-CN/JA)</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="ghost" onClick={load} disabled={loading}
            data-testid="translations-refresh-btn"
            title="Rafraîchir les statuts"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button
            size="sm"
            onClick={translateAll}
            disabled={disabled || runningAll || !!runningLang}
            data-testid="translations-generate-all-btn"
          >
            {runningAll ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Languages className="h-4 w-4 mr-2" />}
            Générer les traductions
          </Button>
        </div>
      </div>
      {disabled && (
        <div className="text-xs text-muted-foreground">
          Le contenu FR n'est pas encore marqué prêt : le bouton reste désactivé.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        {LANGS.map((lg) => {
          const s = statuses[lg];
          const effective = s?.status || "NONE";
          const info = STATUS_LABEL[effective] || STATUS_LABEL.NONE;
          return (
            <div
              key={lg}
              className="border border-border rounded-md p-2 space-y-1"
              data-testid={`translation-item-${lg}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase">{lg}</div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${info.cls}`}>
                  {info.text}
                </span>
              </div>
              {s?.translated_at && (
                <div className="text-[10px] text-muted-foreground truncate">
                  {new Date(s.translated_at).toLocaleDateString("fr-FR")}
                </div>
              )}
              <Button
                variant="outline" size="sm"
                className="w-full text-xs h-7"
                onClick={() => translateOne(lg, effective === "HUMAN_REVIEWED")}
                disabled={disabled || !!runningLang || runningAll}
                data-testid={`translate-btn-${lg}`}
              >
                {runningLang === lg ? <Loader2 className="h-3 w-3 animate-spin" /> :
                  (effective === "NONE" ? "Générer" : effective === "OUTDATED" ? "Régénérer" : "Regénérer")}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
